import { jwtDecode } from "jwt-decode";
import axios from "axios";
import baseURL from "@/http";
// import baseURL from "./http"; // ensure this exports a valid base URL string
import CryptoJS from "crypto-js";
import { releaseServerSession } from "@/services/sessionSignout";
import { clearStorageKeepingLastScreens } from "@/services/lastScreen";

// ===  Token Handling ===

// Check if the access token is expired
const isTokenExpired = (): boolean => {
  const expirationTime = localStorage.getItem("token_expiration");
  if (!expirationTime) return true;
  return Date.now() > parseInt(expirationTime, 10);
};

// Refresh the access token using the refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return null;
    const response = await axios.post(`${baseURL}/token/refresh/`, {
      refresh: refreshToken,
    });

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh; // rotated token

    localStorage.setItem("access_token", newAccessToken);

    const decoded: any = jwtDecode(newAccessToken);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const bufferTime = 2 * 60 * 1000; // 2 minute buffer before actual expiry
    const effectiveExpiration = expirationTime - bufferTime; // Refresh 2 minutes early
    localStorage.setItem("token_expiration", effectiveExpiration.toString());



    if (newRefreshToken) {
      localStorage.setItem("refresh_token", newRefreshToken); // overwrite old one
    }

    return newAccessToken;
  } catch (error) {
    console.error("Token refresh failed", error);
    // The session is over either way; release its slot while the tokens are
    // still here to name it. The endpoint ignores token expiry and refuses
    // (harmlessly) when another device has since taken the slot.
    await releaseServerSession();
    // Force logout, but keep each account's last screen (services/lastScreen).
    clearStorageKeepingLastScreens();
    return null;
  }
};
// Get a valid token (refresh if expired)
export const getValidToken = async (): Promise<string | null> => {
  if (isTokenExpired()) {
    return await refreshAccessToken();
  }
  return localStorage.getItem("access_token");
};

// ===  Session Key Generation ===

export const sessionKeyMap: Record<string, CryptoJS.lib.WordArray> = {};
export const SHARED_SECRET =
  "django-insecure-$9icbs1yp+2k$2tkm$f)pf^sh#vuoq-der9rofbvv954_u)t*f";

export function generateSessionKey() {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1e6);
  const key_id = `client-${timestamp}-${random}`;

  // AES key (32 bytes after hashing)
  const hashedKey = CryptoJS.SHA256(key_id).toString().slice(0, 32);
  const key = CryptoJS.enc.Utf8.parse(hashedKey);
  sessionKeyMap[key_id] = key;

  // HMAC-SHA256 Signature
  const signature = CryptoJS.HmacSHA256(key_id, SHARED_SECRET).toString();

  return { key_id, signature };
}
